/**
 * Testimonial slider: one review shows through a fixed-width window and the
 * full-width slide track slides horizontally to the active index.
 *
 * Loops, in both directions and whether or not it rotates. The track holds a
 * copy of the last review before the first and of the first after the last, so
 * "Next" on the last review slides on to the first as if it came next, instead
 * of rewinding the whole track back past every review; once it has landed on
 * the copy the track swaps to the real one without animating.
 *
 * Rotation stops whenever it would be rude or wasteful:
 *   - the pointer is over the slider, or focus is inside it
 *   - the tab is hidden
 *   - the section has scrolled out of view
 *   - the visitor asked for reduced motion, in which case it never starts
 *   - someone is working the arrows, until they have been idle for a moment
 */
/** Read straight off <html>, so this never depends on oracura-motion.js loading first. */
const motionPaused = () => document.documentElement.dataset.oracuraMotion === 'paused';

class OracuraTestimonials extends HTMLElement {
  /** Set by #watch(); removes the listeners that aren't on this element. */
  #teardownWatchers = null;

  connectedCallback() {
    this.lifecycle?.abort();
    this.lifecycle = new AbortController();
    this.slides = Array.from(this.querySelectorAll('[data-testimonial-slide]'));
    this.track = this.querySelector('[data-testimonial-track]');
    this.prevButton = this.querySelector('[data-testimonial-prev]');
    this.nextButton = this.querySelector('[data-testimonial-next]');
    this.index = 0;
    this.pos = 1;
    this.timer = null;
    this.idle = null;
    this.userTookOver = false;
    // How long the slider waits after the last press before taking over again.
    this.resumeDelay = 6000;

    if (this.slides.length <= 1) {
      if (this.prevButton) this.prevButton.hidden = true;
      if (this.nextButton) this.nextButton.hidden = true;
      return;
    }

    // The two copies that make the ends meet. Marked inert and hidden, and
    // stripped of the slide hook, so nothing counts them, reads them or tabs
    // into them.
    if (this.track && !this.track.querySelector('[data-testimonial-clone]')) {
      const copy = (slide) => {
        const c = slide.cloneNode(true);
        c.removeAttribute('data-testimonial-slide');
        c.removeAttribute('data-shopify-editor-block');
        c.setAttribute('data-testimonial-clone', '');
        c.setAttribute('aria-hidden', 'true');
        c.inert = true;
        return c;
      };
      this.track.prepend(copy(this.slides[this.slides.length - 1]));
      this.track.append(copy(this.slides[0]));
    }
    this.track?.addEventListener('transitionend', (event) => {
      if (event.target === this.track && event.propertyName === 'transform') this.#settle();
    }, { signal: this.lifecycle.signal });

    const seconds = Number.parseFloat(this.getAttribute('autoplay-seconds') || '0');
    this.interval = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;

    // Rotation is motion the visitor did not ask for, so it is exactly what the
    // shared motion preference is meant to suppress. The arrows still work — the
    // slider only stops moving on its own.
    this.autoplay = this.interval > 0;

    // Taking the arrows hands control over, but only for as long as someone is
    // actually using them. Each press stops the rotation and restarts an idle
    // timer; when that runs out with no further presses, rotation resumes. Ending
    // autoplay permanently on the first click meant one curious tap left the
    // slider frozen for the rest of the visit.
    this.prevButton?.addEventListener('click', () => {
      this.handOver();
      this.show(this.index - 1);
    }, { signal: this.lifecycle.signal });
    this.nextButton?.addEventListener('click', () => {
      this.handOver();
      this.show(this.index + 1);
    }, { signal: this.lifecycle.signal });

    if (this.autoplay) this.#watch();

    this.#moveTo(1, false);
    this.show(0, false);
    if (this.autoplay) this.start();
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    this.stop();
    window.clearTimeout(this.idle);
    window.clearTimeout(this.settleTimer);
    this.observer?.disconnect();
    this.#teardownWatchers?.();
  }

  /** Pause triggers: hover, focus, tab visibility and viewport presence. */
  #watch() {
    const pause = () => this.stop();
    const resume = () => this.start();

    this.addEventListener('pointerenter', pause, { signal: this.lifecycle.signal });
    this.addEventListener('pointerleave', resume, { signal: this.lifecycle.signal });
    this.addEventListener('focusin', pause, { signal: this.lifecycle.signal });
    this.addEventListener('focusout', resume, { signal: this.lifecycle.signal });

    const onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', onVisibility);

    // Off-screen sliders should not tick; a long page would otherwise run every
    // carousel on it at once for the whole visit.
    if (typeof IntersectionObserver !== 'undefined') {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.inView = entry.isIntersecting;
            if (entry.isIntersecting) this.start();
            else this.stop();
          }
        },
        { threshold: 0.2 }
      );
      this.observer.observe(this);
    } else {
      this.inView = true;
    }

    // If the preference flips mid-session, honour it immediately — in both
    // directions, so switching motion back on restarts rotation without a
    // reload.
    const onMotionChange = () => {
      if (motionPaused()) {
        this.stop();
        window.clearTimeout(this.idle);
      } else {
        this.start();
      }
    };
    document.addEventListener('oracuramotionchange', onMotionChange);

    this.#teardownWatchers = () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('oracuramotionchange', onMotionChange);
    };
  }

  /**
   * Someone is driving. Stop rotating and wait for them to be done — the timer
   * restarts on every press, so rotation only comes back once they have stopped.
   */
  handOver() {
    this.userTookOver = true;
    this.stop();
    window.clearTimeout(this.idle);
    this.idle = window.setTimeout(() => {
      this.userTookOver = false;
      this.start();
    }, this.resumeDelay);
  }

  start() {
    if (!this.autoplay || this.userTookOver || this.timer) return;
    if (document.hidden || motionPaused()) return;
    if (this.inView === false) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  /** Put the track at a position (0 and count + 1 are the copies). */
  #moveTo(pos, animate) {
    this.pos = pos;
    if (!this.track) return;
    if (!animate) this.track.style.transition = 'none';
    this.track.style.setProperty('--bt-index', String(pos));
    if (!animate) {
      void this.track.offsetWidth;
      this.track.style.transition = '';
    }
  }

  /** Landed on a copy: step to the real review it stands for, unseen. */
  #settle() {
    window.clearTimeout(this.settleTimer);
    const count = this.slides.length;
    if (this.pos === 0 || this.pos === count + 1) this.#moveTo(this.index + 1, false);
  }

  show(nextIndex, animate = true) {
    const count = this.slides.length;

    // A press that arrives while the track is still on a copy settles it first,
    // so a quick double-click cannot walk off the end of the track.
    if (this.pos === 0 || this.pos === count + 1) this.#settle();

    this.index = (nextIndex % count + count) % count;
    const target = nextIndex >= count ? count + 1 : nextIndex < 0 ? 0 : this.index + 1;

    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === this.index ? 'false' : 'true');
    });

    const moving = animate && getComputedStyle(this.track).transitionDuration !== '0s';
    this.#moveTo(target, moving);
    if (target === 0 || target === count + 1) {
      // transitionend does the swap; the timer only covers a transition that
      // never reports back, such as one interrupted by a hidden tab.
      window.clearTimeout(this.settleTimer);
      if (moving) this.settleTimer = window.setTimeout(() => this.#settle(), 700);
      else this.#settle();
    }

    // It loops, so neither end is an end: both controls always work.
    if (this.prevButton) this.prevButton.disabled = false;
    if (this.nextButton) this.nextButton.disabled = false;
  }
}

if (!customElements.get('oracura-testimonials')) {
  customElements.define('oracura-testimonials', OracuraTestimonials);
}
